import { forwardRef, useState } from 'react'
import { ImagePlus, Loader2 } from 'lucide-react'
import { logger } from 'helpers/logger'
import { Upload } from '@wellesley/openapi'
import { cn, UploadFileProps, FormItem, FormMessage } from '@wellesley/ui'
import {
  UploadMediaModal,
  UploadTag,
  useImageSelection,
} from '@wellesley/uploads'

import { CoverImage } from 'components/CoverImage'
import ChangeEventImg from '../ChangeEventImage'

type EventImageFieldProps = {
  value?: Upload | null
  onChange: (imageId: Promise<string> | string | null) => void
}

const getDefaultHeader = (cover?: Upload | null) => {
  return {
    preview: cover?.files?.[0]?.uri || undefined,
    uploadId: cover?.uploadId,
  }
}

export const EventImageField = forwardRef<
  HTMLInputElement,
  EventImageFieldProps
>(({ value, onChange }, ref) => {
  const [cover, setCover] = useState(() => getDefaultHeader(value))
  const [isUploading, setIsUploading] = useState(false)

  const handleUploadFile = async (file: UploadFileProps): Promise<string> => {
    let uploadError: string | undefined

    const uploadId = await uploadFile({
      media: file,
      tag: UploadTag.post,
      onError: (error) => {
        uploadError = error
      },
    })

    if (!uploadId) {
      throw new Error(uploadError || 'Upload failed')
    }

    return uploadId
  }

  const handleImgSave = async ([media]: UploadFileProps[]) => {
    const oldPreview = cover.preview
    setIsUploading(true)

    setCover((img) => ({ ...img, preview: media.objectUrl }))

    try {
      const uploadPromise = handleUploadFile(media)
      onChange(uploadPromise)
      const uploadId = await uploadPromise
      onChange(uploadId)

      setCover((img) => ({ ...img, uploadId }))
    } catch (error) {
      setCover((img) => ({ ...img, preview: oldPreview }))
      logger.error('Error uploading image:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = () => {
    setCover({ preview: '', uploadId: undefined })
    onChange(null)
  }

  const {
    sizes,
    preview,
    acceptList,
    isModalOpen,
    uploadFile,
    handleSave,
    handleClose,
    handleUpload,
    handleFileChange,
  } = useImageSelection((props) => void handleImgSave(props))

  const hasImage = !!cover.preview

  return (
    <>
      <FormItem className="mt-none space-y-xxs">
        <CoverImage
          src={cover.preview}
          data-testid="event-image-preview"
          className={cn('rounded-xl', {
            'opacity-50': isUploading,
          })}
        >
          {!hasImage && (
            <svg className="pointer-events-none absolute inset-0 size-full text-gray-300">
              <rect
                x="1%"
                y="1%"
                width="98%"
                height="98%"
                rx="20"
                ry="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="8 8"
              />
            </svg>
          )}

          <div className="absolute inset-0 flex flex-col items-center justify-center gap-s">
            {!hasImage && <ImagePlus size={40} className="text-gray-700" />}

            <ChangeEventImg
              ref={ref}
              accept={acceptList}
              disabled={isUploading}
              onChange={(props) => {
                void handleUpload(props)
              }}
              {...(hasImage && {
                onDelete: () => void handleDelete(),
              })}
            >
              {hasImage ? 'Change' : 'Upload'} image
            </ChangeEventImg>
          </div>

          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin" />
            </div>
          )}
        </CoverImage>
        <FormMessage />
      </FormItem>

      <UploadMediaModal
        title="Crop image"
        sizes={sizes}
        open={isModalOpen}
        accept={acceptList}
        preview={preview}
        onSave={handleSave}
        onChange={(props) => void handleFileChange(props)}
        onClose={handleClose}
        aspect={1}
      >
        Change cover
      </UploadMediaModal>
    </>
  )
})

EventImageField.displayName = 'EventImageField'
